import React, { useEffect, useRef } from 'react';

import { RideCheckbox as RideCheckboxBase } from '@rimac-seguros/ride-system-components';

import { useValidationDataTable } from '../../utils/hooks/useValidationDataTable';
import { Pagination } from '../Pagination';
import { useTableData } from './TableData.hook';
import { EInputType } from './TableData.types';
import './style.scss';

const RideCheckbox = RideCheckboxBase as React.ComponentType<any>;

type PaginationState = {
	pagina: number;
	tamanio: number;
	totalPaginas: number;
	total: number;
};

type TableDataProps = {
	body?: any[];
	headers?: any[];
	tableName?: string;
	onCellChange?: (...args: unknown[]) => void;
	groupEnabled?: boolean;
	groupByKey?: string;
	radioSelected?: unknown;
	onRadioChange?: (...args: unknown[]) => void;
	enviarCorreoIds?: unknown[];
	onRadioChangeCorreo?: (...args: unknown[]) => void;
	pagination?: PaginationState;
	onPageChange?: (...args: unknown[]) => void;
	excludedStates?: string[];
};

export const TableData = ({
	body = [],
	headers = [],
	tableName,
	onCellChange,
	groupEnabled = false,
	groupByKey,
	radioSelected,
	onRadioChange,
	enviarCorreoIds = [],
	onRadioChangeCorreo,
	pagination = { pagina: 1, tamanio: 10, totalPaginas: 1, total: 0 },
	onPageChange,
	excludedStates = ['Aprobado', 'Rechazado'],
}) => {
	const { currentRows, rowsActive, inputTypeToComponent, handlePageChange } =
		useTableData({
			bodyTableData: body,
			tableName,
		});

	const { handleBuildFormValidations } = useValidationDataTable(tableName);
	const [collapsedGroups, setCollapsedGroups] = React.useState([]);
	const [tableWidth, setTableWidth] = React.useState(1000);
	const currentPage = pagination.pagina;
	const rowsPerPage = pagination.tamanio;
	const _total = pagination.total;
	const paginatedRows = body;
	const startIdx = (currentPage - 1) * rowsPerPage;
	const _endIdx = startIdx + rowsPerPage;

	const topScrollRef = useRef(null);
	const tableScrollRef = useRef(null);

	useEffect(() => {
		const updateWidth = () => {
			if (tableScrollRef.current) {
				setTableWidth(tableScrollRef.current.scrollWidth);
			}
		};
		updateWidth();
		globalThis.addEventListener('resize', updateWidth);
		return () => globalThis.removeEventListener('resize', updateWidth);
	}, [body, headers]);

	useEffect(() => {
		const top = topScrollRef.current;
		const table = tableScrollRef.current;
		if (!top || !table) return;

		const handleTopScroll = () => {
			table.scrollLeft = top.scrollLeft;
		};
		const handleTableScroll = () => {
			top.scrollLeft = table.scrollLeft;
		};

		top.addEventListener('scroll', handleTopScroll);
		table.addEventListener('scroll', handleTableScroll);
		return () => {
			top.removeEventListener('scroll', handleTopScroll);
			table.removeEventListener('scroll', handleTableScroll);
		};
	}, []);

	const groupByMultipleKeys = (rows: any[], keys: string[]) => {
		const groups: Record<string, { values: string[]; rows: any[] }> = {};
		rows.forEach((row: any) => {
			const values = keys.map(
				(k) =>
					row.data.find((cell) => cell.key === k)?.value || 'Sin Iniciativa',
			);
			const groupKey = values.join('||');
			if (!groups[groupKey]) {
				groups[groupKey] = { values, rows: [] };
			}
			groups[groupKey].rows.push(row);
		});
		return groups;
	};

	const renderGroupedBody = (
		groups: Record<string, { values: string[]; rows: any[] }>,
		headers: any[],
	) => {
		return Object.values(groups).map((group) => {
			const groupKey = group.values.join('||');
			const isCollapsed = collapsedGroups.includes(groupKey);

			let groupTitle = '';
			if (group.values.length === 1) {
				groupTitle = group.values[0] || 'Sin Iniciativa';
			} else if (group.values.length === 3) {
				const [iniciativa, codigoPlaneamiento, subIniciativa] = group.values;
				groupTitle = `${iniciativa || 'Sin Iniciativa'} - ${codigoPlaneamiento || 'Sin Código'} - ${subIniciativa || 'Sin Sub Iniciativa'}`;
			} else {
				groupTitle = group.values.filter(Boolean).join(' - ') || 'Sin datos';
			}

			return (
				<React.Fragment key={groupKey}>
					<div
						className="tabledata-row group-header"
						style={{
							fontWeight: 'bold',
							fontSize: '13px',
							display: 'flex',
							width: '100%',
							minWidth: 'max-content',
						}}
					>
						{headers
							.filter(({ hideColumn }) => !hideColumn)
							.map(({ key, width }, index) => {
								const colSpan = 5;
								const shouldSkip = index > 0 && index < colSpan;
								
								if (shouldSkip) return null;
								
								return (
									<div
										key={`group-${key}`}
										style={
											index === 0
												? {
														display: 'flex',
														alignItems: 'center',
														gap: '10px',
														padding: '10px',
														background: '#f5f5f5',
														flex: `0 0 calc(${width}px * ${colSpan})`,
														minWidth: `calc(${width}px * ${colSpan})`,
													}
													: key === 'Comentario'
												? {
														width: '320px',
														minWidth: '320px',
														maxWidth: '320px',
														flex: '0 0 320px',
													}
												: width
													? {
															width: `${width}px`,
															minWidth: `${width}px`,
															flex: `0 0 ${width}px`,
														}
													: {}
									}
								>
									{index === 0 && (
										<>
											<button
												type="button"
												style={{
													cursor: 'pointer',
													background: 'none',
													border: 'none',
													fontSize: '18px',
													flexShrink: 0,
													marginLeft: '10px',
												}}
												onClick={() => {
													setCollapsedGroups((prev) =>
														prev.includes(groupKey)
															? prev.filter((k) => k !== groupKey)
															: [...prev, groupKey],
													);
												}}
											>
												{isCollapsed ? '+' : '-'}
											</button>
											<strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{groupTitle}</strong>
										</>
									)}
								</div>
							);
						})}
					</div>
					{!isCollapsed &&
						group.rows.map((row, idx) => (
							<div
								key={row.idRow+1}
								className={`tabledata-row${idx % 2 === 1 ? ' tabledata-row-alt' : ''}`}
							>
								{headers
									.filter(({ hideColumn }) => !hideColumn)
									.map(
										({
											key,
											inputType,
											options = [],
											validations,
											width,
											prefix,
											suffix,
											render,
										}) => {
											const useRowData = row.data.find(
												(rowData) => rowData.key === key,
											) || { key, value: '' };
											const bodyValidations = useRowData?.validations;
											return (
												<div
													key={`${row.idRow}-${key}`}
													className={`tabledata-cell${key === 'Seleccionar' ? ' tabledata-cell-checkbox' : ''}${inputType === EInputType.DATE ? ' tabledata-cell-date' : ''}${inputType === EInputType.SELECT ? ' tabledata-cell-select' : ''}`}
													style={
														key === 'Comentario'
															? {
																	width: '320px',
																	minWidth: '320px',
																	maxWidth: '320px',
																	flex: '0 0 320px',
																}
															: width
																? {
																		width: `${width}px`,
																		minWidth: `${width}px`,
																		flex: `0 0 ${width}px`,
																	}
																: {}
													}
												>
													{render ? (
														render({
															dataCell: useRowData,
															dataRow: row,
															radioSelected,
															onRadioChange,
															enviarCorreoIds,
															onRadioChangeCorreo,
														})
													) : (
														<div className="w-full">
															{(useRowData.inputType ?? inputType) ? (
																inputTypeToComponent({
																	rowIndex: row.idRow,
																	rowData: row,
																	inputTypeToUse:
																		useRowData.inputType ?? inputType,
																	validations: bodyValidations
																		? handleBuildFormValidations({
																				validations: bodyValidations,
																				isDisabled: useRowData?.disabled,
																			})
																		: (useRowData.inputType ?? inputType) ===
																				EInputType.CHECKBOX
																			? {
																					...validations,
																					selectedRowIds: radioSelected,
																					onChange: () => {
																						if (onRadioChange)
																							onRadioChange(row.idRow);
																					},
																				}
																			: {
																					...validations,
																					onChange: (eventData) => {
																						const value = eventData?.target
																							? eventData.target.value
																							: eventData;
																						if (onCellChange)
																							onCellChange(
																								row.idRow,
																								key,
																								value,
																							);
																					},
																				},
																	name: `miTabla.${row.idRow}.${key}`,
																	options,
																	value: useRowData.value,
																	disabled: useRowData.disabled,
																	decimalNumber:
																		useRowData?.validations?.number?.decimal,
																	prefix:
																		prefix ?? useRowData?.validations?.prefix,
																	suffix:
																		suffix ?? useRowData?.validations?.suffix,
																})
															) : (
																<div className="w-full flex items-center min-h-12.5 whitespace-normal overflow-visible break-words">
																	{key === 'Nueva F. Fin' ? (
																		<span>{useRowData.value || 'NO DATA'}</span>
																	) : (
																		useRowData.value || ''
																	)}
																</div>
															)}
														</div>
													)}
												</div>
											);
										},
									)}
							</div>
						))}
				</React.Fragment>
			);
		});
	};

	return (
		<div style={{ marginTop: '40px' }}>
			<div
				ref={topScrollRef}
				className="tabledata-scroll-container"
				style={{
					width: '100%',
					overflowX: 'auto',
					overflowY: 'hidden',
					height: 16,
					marginBottom: 0,
					WebkitOverflowScrolling: 'touch',
					position: 'relative',
				}}
			>
				<div style={{ width: `${tableWidth}px`, height: 1 }} />
			</div>
			<div
				className="tabledata-scroll-container"
				ref={tableScrollRef}
				style={{
					width: '100%',
					overflowX: 'auto',
					position: 'relative',
					minHeight: '300px',
				}}
			>
				{/* Header */}
				<div className="tabledata-row tabledata-header">
					{headers
						.filter(({ hideColumn }) => !hideColumn)
						.map(({ key, label, width, showSelectAllEmail }) => (
							<div
								key={`${key}-${label}`}
								className={`tabledata-cell${key === 'Seleccionar' ? ' tabledata-cell-checkbox' : ''}`}
								style={
									key === 'Comentario'
										? {
												width: '320px',
												minWidth: '320px',
												maxWidth: '320px',
												flex: '0 0 320px',
											}
										: width
											? {
													width: `${width}px`,
													minWidth: `${width}px`,
													flex: `0 0 ${width}px`,
												}
											: {}
								}
							>
								{key === 'Seleccionar' ? (
									<RideCheckbox
										name="headerSeleccionar"
										checked={
											paginatedRows.length > 0 &&
											paginatedRows
												.filter((row) => {
													const estado = row.data.find(
														(cell) => cell.key === 'Estado',
													)?.value;
													return !excludedStates.includes(estado);
												})
												.every((row) => radioSelected.includes(row.idRow))
										}
										indeterminate={
											paginatedRows.some((row) => {
												const estado = row.data.find(
													(cell) => cell.key === 'Estado',
												)?.value;
												return (
													!excludedStates.includes(estado) &&
													radioSelected.includes(row.idRow)
												);
											}) &&
											!paginatedRows
												.filter((row) => {
													const estado = row.data.find(
														(cell) => cell.key === 'Estado',
													)?.value;
													return !excludedStates.includes(estado);
												})
												.every((row) => radioSelected.includes(row.idRow))
										}
										onChange={() => {
											const selectableIds = paginatedRows
												.filter((row) => {
													const estado = row.data.find(
														(cell) => cell.key === 'Estado',
													)?.value;
													return !excludedStates.includes(estado);
												})
												.map((row) => String(row.idRow));
											const allSelected =
												selectableIds.length > 0 &&
												selectableIds.every((id) => radioSelected.includes(id));
											if (allSelected) {
												onRadioChange(
													radioSelected.filter(
														(id) => !selectableIds.includes(String(id)),
													),
												);
											} else {
												onRadioChange([
													...radioSelected.filter(
														(id) => !selectableIds.includes(String(id)),
													),
													...selectableIds,
												]);
											}
										}}
									/>
								) : (
									<div>
										<div className="w-full whitespace-normal overflow-visible break-words">
											{label}
										</div>

										{showSelectAllEmail && (
											<RideCheckbox
												name={`header-${key}`}
												checked={
													paginatedRows.length > 0 &&
													paginatedRows
														.filter((row) => {
															const estado = row.data.find(
																(cell) => cell.key === 'Estado',
															)?.value;
															return !excludedStates.includes(estado);
														})
														.every((row) => {
															const checkArray =
																key === 'EnviarCorreo'
																	? enviarCorreoIds
																	: radioSelected;
															return checkArray.includes(row.idRow);
														})
												}
												indeterminate={
													paginatedRows.some((row) => {
														const estado = row.data.find(
															(cell) => cell.key === 'Estado',
														)?.value;
														const checkArray =
															key === 'EnviarCorreo'
																? enviarCorreoIds
																: radioSelected;
														return (
															!excludedStates.includes(estado) &&
															checkArray.includes(row.idRow)
														);
													}) &&
													!paginatedRows
														.filter((row) => {
															const estado = row.data.find(
																(cell) => cell.key === 'Estado',
															)?.value;
															return !excludedStates.includes(estado);
														})
														.every((row) => {
															// CORRECCIÓN: Usar el array correcto según la columna
															const checkArray =
																key === 'EnviarCorreo'
																	? enviarCorreoIds
																	: radioSelected;
															return checkArray.includes(row.idRow);
														})
												}
												onChange={() => {
													const selectableIds = paginatedRows
														.filter((row) => {
															const estado = row.data.find(
																(cell) => cell.key === 'Estado',
															)?.value;
															return !excludedStates.includes(estado);
														})
														.map((row) => String(row.idRow));

													if (key === 'EnviarCorreo') {
														const allSelected =
															selectableIds.length > 0 &&
															selectableIds.every((id) =>
																enviarCorreoIds.includes(id),
															);
														if (allSelected) {
															selectableIds.forEach((id) => {
																if (enviarCorreoIds.includes(id)) {
																	onRadioChangeCorreo(id);
																}
															});
														} else {
															// Seleccionar todos los que no están seleccionados
															selectableIds.forEach((id) => {
																if (!enviarCorreoIds.includes(id)) {
																	onRadioChangeCorreo(id);
																}
															});
														}
													} else {
														const allSelected =
															selectableIds.length > 0 &&
															selectableIds.every((id) =>
																radioSelected.includes(id),
															);
														if (allSelected) {
															onRadioChange(
																radioSelected.filter(
																	(id) => !selectableIds.includes(String(id)),
																),
															);
														} else {
															onRadioChange([
																...radioSelected.filter(
																	(id) => !selectableIds.includes(String(id)),
																),
																...selectableIds,
															]);
														}
													}
												}}
											/>
										)}
									</div>
								)}
							</div>
						))}
				</div>
				{/* Body */}
				<div>
					{groupEnabled && Array.isArray(groupByKey)
						? renderGroupedBody(
								groupByMultipleKeys(paginatedRows, groupByKey),
								headers,
							)
						: paginatedRows.map((bodyProps, rowIndex) => (
								<div
									key={bodyProps.idRow}
									className={`tabledata-row${rowIndex % 2 === 1 ? ' tabledata-row-alt' : ''}`}
								>
									{headers
										.filter(({ hideColumn }) => !hideColumn)
										.map(
											({
												key,
												inputType,
												options = [],
												validations,
												width,
												prefix,
												suffix,
												render,
											}) => {
												const useRowData = bodyProps.data.find(
													(rowData) => rowData.key === key,
												) || {
													key,
													value: '',
												};
												const bodyValidations = useRowData?.validations;
												return (
													<div
														key={`${bodyProps.idRow}-${key}`}
														className={`tabledata-cell${key === 'Seleccionar' ? ' tabledata-cell-checkbox' : ''}${inputType === EInputType.DATE ? ' tabledata-cell-date' : ''}${inputType === EInputType.SELECT ? ' tabledata-cell-select' : ''}`}
														style={
															key === 'Comentario'
																? {
																		width: '320px',
																		minWidth: '320px',
																		maxWidth: '320px',
																		flex: '0 0 320px',
																	}
																: width
																	? {
																			width: `${width}px`,
																			minWidth: `${width}px`,
																			flex: `0 0 ${width}px`,
																		}
																	: {}
														}
													>
														{render ? (
															render({
																dataCell: useRowData,
																dataRow: bodyProps,
																radioSelected,
																onRadioChange,
																enviarCorreoIds,
																onRadioChangeCorreo,
															})
														) : (
															<div className="w-full">
																{(useRowData.inputType ?? inputType) ? (
																	inputTypeToComponent({
																		rowIndex: bodyProps.idRow,
																		rowData: bodyProps,
																		inputTypeToUse:
																			useRowData.inputType ?? inputType,
																		validations: bodyValidations
																			? handleBuildFormValidations({
																					validations: bodyValidations,
																					isDisabled: useRowData?.disabled,
																				})
																			: (useRowData.inputType ?? inputType) ===
																					EInputType.CHECKBOX
																				? {
																						...validations,
																						selectedRowIds: radioSelected,
																						onChange: () => {
																							if (onRadioChange)
																								onRadioChange(bodyProps.idRow);
																						},
																					}
																				: {
																						...validations,
																						onChange: (eventData) => {
																							const value = eventData?.target
																								? eventData.target.value
																								: eventData;
																							if (onCellChange)
																								onCellChange(
																									bodyProps.idRow,
																									key,
																									value,
																								);
																						},
																					},
																		name: `miTabla.${bodyProps.idRow}.${key}`,
																		options,
																		value: useRowData.value,
																		disabled: useRowData.disabled,
																		decimalNumber:
																			useRowData?.validations?.number?.decimal,
																		prefix:
																			prefix ?? useRowData?.validations?.prefix,
																		suffix:
																			suffix ?? useRowData?.validations?.suffix,
																	})
																) : (
																	<div className="w-full flex items-center min-h-12.5 whitespace-normal overflow-visible break-words">
																		{key === 'Nueva F. Fin' ? (
																			<span>{useRowData.value || '-'}</span>
																		) : (
																			useRowData.value || ''
																		)}
																	</div>
																)}
															</div>
														)}
													</div>
												);
											},
										)}
								</div>
							))}
				</div>
			</div>
			{/* Pagination */}
			<div className="tabledata-footer">
				<Pagination
					postPerPage={pagination.tamanio}
					totalRows={pagination.total}
					paginate={onPageChange}
					rowsQty={body.length}
					page={pagination.pagina}
					message={null}
				/>
			</div>
		</div>
	);
};
